import type { TUser } from "@/shared/services";
import { formatDate } from "@pages/analytics/utils";
import { IconUserQuestion, IconX } from "@tabler/icons-react";

type TVersionDetailsHeaderProps = {
  versionName?: string;
  storyName?: string;
  createdAt?: string | null;
  owner?: TUser | null;
  onClose?: () => void;
};

const getInitials = (name?: string) => {
  if (!name) return "";
  const words = name.split(" ").filter(Boolean);
  return words
    .slice(0, 2)
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
};

export const VersionDetailsHeader = ({
  versionName,
  storyName,
  createdAt,
  owner,
  onClose,
}: TVersionDetailsHeaderProps) => {
  return (
    <div className="tw-py-4 tw-grid tw-grid-cols-[minmax(0,1fr)_200px_200px_200px] tw-items-center tw-gap-4 tw-relative -lg:tw-grid-cols-2 -lg:tw-p-0">
      <div className="tw-flex tw-flex-col tw-gap-2 tw-min-w-0 -lg:tw-gap-1 -lg:tw-col-span-2 -lg:tw-mb-2">
        <span className="tw-text-blue-500 tw-text-lg tw-font-normal tw-font-sans tw-block -lg:tw-text-sm">
          Version details
        </span>
        <h2 className="tw-text-2xl tw-font-semibold tw-font-sans tw-text-blue -lg:tw-text-xl tw-truncate tw-max-w-full tw-min-w-0">
          {versionName || "Not available"}
        </h2>
      </div>
      <div className="-lg:tw-py-2">
        <p className="tw-mt-0 tw-mb-1.5 tw-font-sans tw-text-sm tw-font-semibold tw-text-blue">
          Story
        </p>
        <p className="tw-my-0 tw-font-sans tw-text-sm tw-truncate tw-font-normal tw-text-blue">
          {storyName || "Not available"}
        </p>
      </div>
      <div className="-lg:tw-py-2">
        <p className="tw-mt-0 tw-mb-1.5 tw-font-sans tw-text-sm tw-font-semibold tw-text-blue">
          Created at
        </p>
        <p className="tw-my-0 tw-font-sans tw-text-sm tw-truncate tw-font-normal tw-text-blue">
          {createdAt ? formatDate(createdAt) : "Not available"}
        </p>
      </div>
      <div className="-lg:tw-py-2">
        <p className="tw-mt-0 tw-mb-1.5 tw-font-sans tw-text-sm tw-font-semibold tw-text-blue">
          Owner
        </p>
        <div className="tw-flex tw-items-center tw-gap-2">
          <div className="tw-size-6 tw-shrink-0 tw-rounded-full tw-border tw-border-solid tw-border-blue/10 tw-flex tw-items-center tw-justify-center tw-bg-blue-50 tw-overflow-hidden">
            {!owner && (
              <IconUserQuestion className="tw-size-3 tw-text-blue/50" />
            )}
            {owner?.firstName && owner?.profile?.picture ? (
              <img
                src={owner.profile.picture}
                alt={owner.firstName}
                className="tw-size-full tw-object-cover"
              />
            ) : (
              owner?.firstName && (
                <span className="tw-text-xs tw-text-blue-500 tw-font-sans tw-font-medium">
                  {getInitials(owner.firstName)}
                </span>
              )
            )}
          </div>
          <p className="tw-my-0 tw-font-sans tw-text-sm tw-truncate tw-font-normal tw-text-blue tw-min-w-0">
            {owner?.firstName || "Not available"}
          </p>
        </div>
      </div>
      <button
        type="button"
        className="tw-flex tw-cursor-pointer tw-items-center tw-group/close tw-justify-center tw-absolute tw-top-1.5 tw-right-0"
        onClick={onClose}
      >
        <IconX className="tw-size-4 tw-text-blue-500 group-hover/close:tw-text-blue tw-transition-colors" />
      </button>
    </div>
  );
};
